import { CanAccess } from '@modules/common/components/CanAccess';
import { ConnectIntegrationCTA } from '@modules/integrationsV2/components/ConnectIntegrationCTA';
import { EditIntegrationCTA } from '@modules/integrationsV2/components/EditIntegrationCTA';
import { FileUploader } from '@modules/integrationsV2/components/FileUploader';
import { IntegrationSourceMoreActionsMenu } from '@modules/integrationsV2/components/IntegrationsContainer/IntegrationsSourceDetailView/IntegrationSourceMoreActionsMenu';
import { useFetchRecordCountForCohortGroup } from '@modules/integrationsV2/hooks';
import { IntegrationSource } from '@modules/integrationsV2/types';
import { trackIntegrationPageEvent } from '@modules/integrationsV2/utils';
import { HelpOutlineRounded, InfoOutlined } from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationType,
  IntegrationViewFragment,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { getInstructionsUrlForChannel } from 'modules/ui/Settings/Integrations/utils/getInstructionsUrlForChannel';
import { ReactNode } from 'react';
import useGetIntegrationsFormData from 'v2/core/repository/edge-server/integrations/useGetIntegrationsFormData';
import { IsLoading } from 'v2/core/repository/edge-server/types';

interface IntegrationSourceActionsProps {
  integrations: IntegrationViewFragment[];
  integrationSource: IntegrationSource;
}

export const IntegrationSourceActions = ({
  integrations,
  integrationSource,
}: IntegrationSourceActionsProps) => {
  const { data: integrationsFormData, status: integrationsFormDataStatus } =
    useGetIntegrationsFormData({
      fetchPolicy: 'cache-first', // Because we have "cache-and-network" on the root component, we can use "cache-first" here.
    });

  const {
    integrationID,
    channel,
    integrationType,
    namespace,
    isDisabled,
    isSingleConnectionSource,
  } = integrationSource;

  const isFileUploadIntegration =
    channel.type === IntegrationChannelType.FileUpload;

  const { recordCount: cohortGroupRecordCount } =
    useFetchRecordCountForCohortGroup({
      cohortName: namespace ?? '',
      skip:
        integrationType !== IntegrationType.Cohorts ||
        channel.type !== IntegrationChannelType.FileUpload ||
        !namespace,
      fetchPolicy: 'cache-first', // we already have the data in the cache
    });

  const currentIntegration = isSingleConnectionSource
    ? integrations.find((integration) => integration.id === integrationID)
    : null;

  const isEmptyFileUploadIntegration =
    currentIntegration?.data.fileUpload?.isEmpty;

  const getSingleIntegrationViewActions = () => {
    const actions: ReactNode[] = [];

    const showUploadButton =
      integrationType === IntegrationType.Cohorts
        ? !!cohortGroupRecordCount
        : !isEmptyFileUploadIntegration;

    if (isFileUploadIntegration && showUploadButton) {
      actions.push(
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip arrow title="See instructions" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                window.open(
                  getInstructionsUrlForChannel(
                    {
                      type: IntegrationChannelType.FileUpload,
                    },
                    integrationType,
                  ),
                );
              }}
            >
              <HelpOutlineRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          {currentIntegration && (
            <Stack direction="row" alignItems="center">
              <CanAccess
                operation={PermissionOperation.Write}
                resource={PermissionResource.Integration}
              >
                <FileUploader
                  id={currentIntegration.id}
                  integrationType={integrationType}
                  buttonLabel="Re-upload File"
                  isReupload
                />
              </CanAccess>
              {integrationType === IntegrationType.Cohorts && (
                <Tooltip
                  arrow
                  title="Once you re-upload a file, it might take up to 4 hours for all new feedback matches to get added to the cohort."
                >
                  <InfoOutlined
                    sx={{
                      color: 'text.secondary',
                      fontSize: '1rem',
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>,
      );
    }

    if (currentIntegration) {
      actions.push(
        <EditIntegrationCTA
          integration={currentIntegration}
          buttonVariant="outlined"
        />,
      );
    }

    if (!isDisabled) {
      actions.push(
        <IntegrationSourceMoreActionsMenu
          integrationSource={integrationSource}
        />,
      );
    }

    return actions;
  };

  const getMultiIntegrationViewActions = () => {
    const actions: ReactNode[] = [];

    const selectedSourceFormData = integrationsFormData?.find(
      (integrationForm) =>
        integrationForm.channel.type === channel.type &&
        integrationForm.integrationType === integrationType,
    );

    actions.push(
      <ConnectIntegrationCTA
        isDrawerView={false}
        integrationChannel={channel}
        buttonLabel="Add New"
        oAuthURL={
          selectedSourceFormData?.__typename === 'OAuthFormData'
            ? selectedSourceFormData.oAuthURL
            : undefined
        }
        isDisabled={IsLoading(integrationsFormDataStatus)}
        integrationType={integrationType}
        onClickConnect={() => {
          trackIntegrationPageEvent({
            eventName: 'Integrations - Add New Button From Toolbar Clicked',
            integration: {
              integrationType: integrationType,
              channelType: channel.type,
              namespace: namespace,
            },
          });
        }}
      />,
    );

    if (!isDisabled) {
      actions.push(
        <IntegrationSourceMoreActionsMenu
          integrationSource={integrationSource}
        />,
      );
    }

    return actions;
  };

  const actions = isSingleConnectionSource
    ? getSingleIntegrationViewActions()
    : getMultiIntegrationViewActions();

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {actions.map((action, index) => (
        <Box key={index}>{action}</Box>
      ))}
    </Stack>
  );
};
